// O TEXTO CITADO de uma resposta: onde ele começa, para a tela poder recolhê-lo.
//
// Pedido do Jhou, olhando uma conversa de quatro mensagens: cada resposta
// trazia embaixo, citada, a mensagem inteira que ela respondia — e essa
// mensagem já estava aberta logo acima. O Gmail esconde isso atrás de "•••";
// aqui também.
//
// NADA É APAGADO. A citação é parte da mensagem (quem responde de outro
// programa a vê inteira); o que muda é só o que a tela mostra de cara. Por
// isso, na dúvida, NÃO se corta: um corte errado esconde texto que a pessoa
// escreveu, e um corte que faltou só deixa a tela mais comprida.
//
// As formas reconhecidas são as que aparecem de verdade nas caixas daqui:
//   - a nossa e a da maioria dos programas: "Em <data>, <quem> escreveu:"
//     seguida de linhas com ">" (inglês: "On <data>, <quem> wrote:");
//   - a do Outlook: uma linha de sublinhados e o bloco "De: / Enviado: /
//     Para: / Assunto:" — sem ">" nenhum, o texto citado vem cru embaixo;
//   - a do Outlook antigo: "-----Mensagem original-----".
//
// ENCAMINHAMENTO NÃO É CITAÇÃO. "---------- Mensagem encaminhada ----------"
// marca o CONTEÚDO que se quis mandar; escondê-lo seria abrir um "segue o
// boleto" e não ver o boleto. Quem chama decide pelo assunto
// (`ehEncaminhamento`), e as marcas de encaminhamento não estão aqui.

/** "Em 15 de set. de 2026, 14:52, Fulano escreveu:" numa linha só. */
const ATRIBUICAO = /^(em|on|le|am|el|il)\b.*\d.*\b(escreveu|wrote|a écrit|schrieb|escribió|ha scritto)\s*:$/i;
/** O começo de uma atribuição que o programa quebrou em duas linhas. */
const ATRIBUICAO_INICIO = /^(em|on|le|am|el|il)\b.*\d/i;
const ATRIBUICAO_FIM = /\b(escreveu|wrote|a écrit|schrieb|escribió|ha scritto)\s*:$/i;
/** O bloco de cabeçalho que o Outlook põe sobre a mensagem citada. */
const CABECALHO = /^(de|from)\s*:/i;
const SUBLINHADOS = /^_{8,}$/;
const ORIGINAL = /^-{3,}\s*(mensagem original|original message)\s*-{3,}$/i;

export interface Separado {
  principal: string;
  /** Vazio quando não há citação reconhecível. */
  citacao: string;
}

/** Todas as linhas não vazias a partir de `de` começam com ">" (e há ao menos uma). */
function citadoDaqui(linhas: string[], de: number): boolean {
  const resto = linhas.slice(de).filter((l) => l.trim() !== "");
  return resto.length > 0 && resto.every((l) => l.trimStart().startsWith(">"));
}

function proximaNaoVazia(linhas: string[], de: number): string {
  for (let i = de; i < linhas.length; i++) {
    const l = (linhas[i] ?? "").trim();
    if (l !== "") return l;
  }
  return "";
}

/** Linha em que a citação começa, ou -1. */
function inicioNoTexto(linhas: string[]): number {
  for (let i = 0; i < linhas.length; i++) {
    const l = (linhas[i] ?? "").trim();
    if (ATRIBUICAO.test(l) && citadoDaqui(linhas, i + 1)) return i;
    if (
      ATRIBUICAO_INICIO.test(l) &&
      i + 1 < linhas.length &&
      ATRIBUICAO_FIM.test((linhas[i + 1] ?? "").trim()) &&
      citadoDaqui(linhas, i + 2)
    )
      return i;
    // ">" solto no meio, com texto da pessoa DEPOIS, é resposta intercalada:
    // `citadoDaqui` exige que tudo até o fim seja citação.
    if (l.startsWith(">") && citadoDaqui(linhas, i)) return i;
    if (SUBLINHADOS.test(l) && CABECALHO.test(proximaNaoVazia(linhas, i + 1))) return i;
    if (ORIGINAL.test(l)) return i;
  }
  return -1;
}

/** Separa o que a pessoa escreveu do que ela citou, num corpo de texto puro. */
export function separarCitacao(texto: string | null | undefined): Separado {
  const t = texto ?? "";
  const linhas = t.replace(/\r\n?/g, "\n").split("\n");
  const inicio = inicioNoTexto(linhas);
  if (inicio < 0) return { principal: t, citacao: "" };
  const principal = linhas.slice(0, inicio).join("\n").replace(/\s+$/, "");
  // Mensagem que é SÓ citação (um encaminhamento sem comentário, uma
  // resposta em branco): esconder tudo deixaria a tela vazia.
  if (principal.trim() === "") return { principal: t, citacao: "" };
  return { principal, citacao: linhas.slice(inicio).join("\n") };
}

// O RESUMO é uma linha só, já sem quebras: as marcas são procuradas no meio.
const CORTES_DO_RESUMO = [
  /\s(em|on|le|am|el)\s[^:]{0,60}\d[\s\S]{0,160}?\b(escreveu|wrote|a écrit|schrieb|escribió)\s*:/i,
  /(?:^|\s)_{8,}/,
  /(?:^|\s)-{3,}\s*(mensagem original|original message)/i,
  /\s(de|from):\s[\s\S]{1,200}?\s(enviad[oa]|sent|data|date):/i,
];

/**
 * O resumo da linha sem o pedaço citado: "blz", e não "blz Em 15 de set. de
 * 2026, 14:52, Fulano escreveu: recebido ____ De: …".
 */
export function resumoSemCitacao(resumo: string | null | undefined): string {
  const r = resumo ?? "";
  let corte = r.length;
  for (const marca of CORTES_DO_RESUMO) {
    const achado = marca.exec(r);
    if (achado && achado.index < corte) corte = achado.index;
  }
  const limpo = r.slice(0, corte).trim();
  // Sem uma letra sequer, o que sobrou é moldura da citação (sublinhados,
  // traços, ">"), não texto da pessoa: melhor o resumo inteiro.
  return /[\p{L}\p{N}]/u.test(limpo) ? limpo : r;
}

/** "Enc:", "Fwd:", "FW:", "TR:" — o conteúdo citado É a mensagem. */
export function ehEncaminhamento(assunto: string | null | undefined): boolean {
  return /^\s*(re:\s*)*(enc|fwd?|tr|wg)\s*:/i.test(assunto ?? "");
}

// ── HTML ────────────────────────────────────────────────────────────────────
//
// No HTML não se corta o TEXTO: marca-se, no DOM já desenhado, cada nó da
// citação com `data-citacao`, e uma regra do `tema.css` os esconde enquanto o
// contêiner tiver a classe `citacao-oculta`. Reescrever o HTML (parsear,
// cortar, serializar de novo) seria reinterpretar conteúdo hostil que o
// servidor já higienizou — e o que se reinterpreta é por onde a higienização
// se perde.

const SELETORES_CONHECIDOS = [
  ".gmail_quote", // Gmail
  "#divRplyFwdMsg", // Outlook (o bloco De:/Enviado:)
  "#appendonsend", // Outlook (o marcador logo antes dele)
  ".moz-cite-prefix", // Thunderbird ("Em … escreveu:")
  "blockquote[type=cite]", // Apple Mail e Thunderbird
].join(",");

function textoDe(no: Node | null): string {
  return (no?.textContent ?? "").replace(/\s+/g, " ").trim();
}

/** O primeiro nó "de verdade" depois de `no`, em ordem de documento, dentro de `raiz`. */
function seguinteComTexto(no: Node, raiz: Node): Node | null {
  for (let atual: Node | null = no; atual && atual !== raiz; atual = atual.parentNode) {
    for (let n = atual.nextSibling; n; n = n.nextSibling) if (textoDe(n) !== "") return n;
  }
  return null;
}

function anteriorComTexto(no: Node): Node | null {
  for (let n = no.previousSibling; n; n = n.previousSibling) if (textoDe(n) !== "") return n;
  return null;
}

/** Há texto da PESSOA depois de `no`? (resposta intercalada, ou escrita embaixo) */
function haTextoDepois(no: Node, raiz: Node): boolean {
  return seguinteComTexto(no, raiz) !== null;
}

/** O nó em que a citação começa, ou null. Não altera nada. */
export function inicioNoHtml(raiz: HTMLElement): Node | null {
  const candidatos: Node[] = [];

  const conhecido = raiz.querySelector(SELETORES_CONHECIDOS);
  if (conhecido) candidatos.push(conhecido);

  // Outlook com os atributos tirados pela higienização: <hr> e, logo depois,
  // "De: … Enviado: …".
  for (const hr of Array.from(raiz.querySelectorAll("hr"))) {
    if (CABECALHO.test(textoDe(seguinteComTexto(hr, raiz)))) {
      candidatos.push(hr);
      break;
    }
  }

  // <blockquote> precedido da atribuição — e só se NADA vier depois dele.
  // Com texto depois, é resposta intercalada: esconder levaria junto o que a
  // pessoa escreveu entre os trechos citados.
  for (const bq of Array.from(raiz.querySelectorAll("blockquote"))) {
    const antes = anteriorComTexto(bq);
    if (antes && ATRIBUICAO_FIM.test(textoDe(antes)) && !haTextoDepois(bq, raiz)) {
      candidatos.push(antes);
      break;
    }
  }

  // "-----Mensagem original-----" num parágrafo próprio.
  for (const el of Array.from(raiz.querySelectorAll("p, div, span"))) {
    if (el.children.length === 0 && ORIGINAL.test(textoDe(el))) {
      candidatos.push(el);
      break;
    }
  }

  if (candidatos.length === 0) return null;
  // O que vier PRIMEIRO no documento.
  candidatos.sort((a, b) =>
    a === b ? 0 : a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
  );
  const inicio = candidatos[0];
  if (!inicio) return null;

  // Só citação, nada escrito antes: não se esconde (mesma regra do texto).
  const antes = raiz.ownerDocument.createRange();
  antes.setStart(raiz, 0);
  antes.setEndBefore(inicio);
  if (antes.toString().trim() === "" && !antes.cloneContents().querySelector("img")) return null;

  return inicio;
}

/**
 * Marca com `data-citacao` o nó `inicio` e tudo o que vem depois dele dentro
 * de `raiz`. Texto solto (que não aceita atributo) ganha um <span> em volta.
 */
export function marcarCitacao(raiz: HTMLElement, inicio: Node): void {
  const nos: Node[] = [inicio];
  for (let atual: Node | null = inicio; atual && atual !== raiz; atual = atual.parentNode) {
    for (let n = atual.nextSibling; n; n = n.nextSibling) nos.push(n);
  }
  for (const no of nos) {
    if (no.nodeType === Node.ELEMENT_NODE) {
      (no as Element).setAttribute("data-citacao", "");
    } else if (no.nodeType === Node.TEXT_NODE && textoDe(no) !== "") {
      const embrulho = raiz.ownerDocument.createElement("span");
      embrulho.setAttribute("data-citacao", "");
      no.parentNode?.insertBefore(embrulho, no);
      embrulho.appendChild(no);
    }
  }
}
